/* AISLPC Controller Tester V2.2.3
 * RGpad02 digital-mode profile.
 * Task-oriented layout: controller feedback stays on the left, while pending
 * buttons move into a compact tested list on the right after first activation.
 */
(() => {
  'use strict';

  if (window.__AISLPC_CONTROLLER_TESTER_V2__) return;
  window.__AISLPC_CONTROLLER_TESTER_V2__ = true;

  const BUTTON_LABELS = ['X', 'A', 'B', 'Y', 'L1', 'R1', 'L2', 'R2', 'SELECT', 'START', 'L3', 'R3'];
  const IMPORTANT_AXES = [0, 1, 2, 5, 9];
  const TOTAL_TEST_BUTTONS = BUTTON_LABELS.length;
  const STICK_CENTERS = {
    left: { cx: 3067.29, cy: 8356.42, r: 1010.2 },
    right: { cx: 8785.48, cy: 8356.42, r: 1010.2 }
  };

  const instances = new Map();

  class AISLPCControllerTester {
    constructor(root) {
      this.root = root;
      this.destroyed = false;
      this.selectedGamepadIndex = null;
      this.rafId = 0;
      this.toastTimer = 0;
      this.testedButtons = new Set();
      this.lastButtonCount = -1;
      this.lastAxisCount = -1;
      this.currentPadSignature = '';
      this.rawButtonRows = [];
      this.rawAxisRows = [];

      this.render = this.render.bind(this);
      this.onSelectChange = this.onSelectChange.bind(this);
      this.onReset = this.onReset.bind(this);
      this.onGamepadConnected = this.onGamepadConnected.bind(this);
      this.onGamepadDisconnected = this.onGamepadDisconnected.bind(this);
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      this.onDiagnosticToggle = this.onDiagnosticToggle.bind(this);

      this.config = this.readConfig();
      this.cacheElements();
      this.cacheMappings();
      this.bindEvents();
      this.updateChecklist();
      this.clearVisuals();

      if (typeof navigator.getGamepads !== 'function') {
        this.setAccessError(this.config.unsupported);
        return;
      }

      const pads = this.refreshGamepadSelect();
      if (pads && pads.length) this.startLoop();
    }

    readConfig() {
      const node = this.root.querySelector('[data-act-config]');
      if (!node) return {};
      try {
        return JSON.parse(node.textContent || '{}');
      } catch (error) {
        console.warn('[AISLPC Controller Tester] Invalid component config.', error);
        return {};
      }
    }

    cacheElements() {
      const query = (selector) => this.root.querySelector(selector);

      this.el = {
        gamepadSelect: query('[data-gamepad-select]'),
        connectionPill: query('[data-connection-pill]'),
        connectionState: query('[data-connection-state]'),
        connectionHint: query('[data-connection-hint]'),
        buttonProgress: query('[data-button-progress]'),
        buttonProgressBar: query('[data-button-progress-bar]'),
        resetButton: query('[data-reset-button]'),
        leftReadout: query('[data-left-readout]'),
        rightReadout: query('[data-right-readout]'),
        dpadReadout: query('[data-dpad-readout]'),
        leftMonitorDot: query('[data-left-monitor-dot]'),
        rightMonitorDot: query('[data-right-monitor-dot]'),
        leftAxisValues: query('[data-left-axis-values]'),
        rightAxisValues: query('[data-right-axis-values]'),
        axis9Direction: query('[data-axis9-direction]'),
        axis9Value: query('[data-axis9-value]'),
        activeAxisCount: query('[data-active-axis-count]'),
        gamepadId: query('[data-gamepad-id]'),
        gamepadMapping: query('[data-gamepad-mapping]'),
        buttonCount: query('[data-button-count]'),
        axisCount: query('[data-axis-count]'),
        rawButtons: query('[data-raw-buttons]'),
        rawAxes: query('[data-raw-axes]'),
        diagnosticDetails: query('.act-diagnostic-details'),
        pendingList: query('[data-pending-list]'),
        completedList: query('[data-completed-list]'),
        completedSection: query('[data-completed-section]'),
        pendingCount: query('[data-pending-count]'),
        completedCount: query('[data-completed-count]'),
        pendingEmpty: query('[data-pending-empty]'),
        toast: query('[data-toast]')
      };

      this.checkItems = new Map(
        Array.from(this.root.querySelectorAll('[data-check-button]')).map((node) => [Number(node.dataset.checkButton), node])
      );

      this.axisRows = new Map(
        Array.from(this.root.querySelectorAll('[data-axis-row]')).map((node) => [
          Number(node.dataset.axisRow),
          { row: node, bar: node.querySelector('[data-axis-bar]'), value: node.querySelector('[data-axis-value]') }
        ])
      );
    }

    cacheMappings() {
      this.parts = new Map(
        Array.from(this.root.querySelectorAll('[data-gp-part]')).map((node) => [node.dataset.gpPart, node])
      );
      this.highlightParts = Array.from(this.parts.values());
      this.stickDots = {
        left: this.root.querySelector('[data-stick-dot="left"]'),
        right: this.root.querySelector('[data-stick-dot="right"]')
      };
    }

    bindEvents() {
      this.el.gamepadSelect?.addEventListener('change', this.onSelectChange);
      this.el.resetButton?.addEventListener('click', this.onReset);
      this.el.diagnosticDetails?.addEventListener('toggle', this.onDiagnosticToggle);
      window.addEventListener('gamepadconnected', this.onGamepadConnected);
      window.addEventListener('gamepaddisconnected', this.onGamepadDisconnected);
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    }

    destroy() {
      if (this.destroyed) return;
      this.destroyed = true;
      this.stopLoop();
      window.clearTimeout(this.toastTimer);
      this.el.gamepadSelect?.removeEventListener('change', this.onSelectChange);
      this.el.resetButton?.removeEventListener('click', this.onReset);
      this.el.diagnosticDetails?.removeEventListener('toggle', this.onDiagnosticToggle);
      window.removeEventListener('gamepadconnected', this.onGamepadConnected);
      window.removeEventListener('gamepaddisconnected', this.onGamepadDisconnected);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }

    getGamepads() {
      try {
        return navigator.getGamepads();
      } catch (error) {
        this.setAccessError(this.config.blocked);
        console.warn('[AISLPC Controller Tester] Gamepad access failed.', error);
        return null;
      }
    }

    collectGamepads() {
      const list = this.getGamepads();
      if (!list) return null;
      return Array.from(list).filter(Boolean);
    }

    refreshGamepadSelect() {
      const pads = this.collectGamepads();
      if (!pads) return null;

      const previous = this.selectedGamepadIndex;
      const fragment = document.createDocumentFragment();
      if (this.el.gamepadSelect) this.el.gamepadSelect.textContent = '';

      if (!pads.length) {
        const option = document.createElement('option');
        option.value = '';
        option.textContent = this.config.waiting || 'Waiting for controller';
        fragment.appendChild(option);
        this.selectedGamepadIndex = null;
        this.el.gamepadSelect?.appendChild(fragment);
        if (this.el.gamepadSelect) this.el.gamepadSelect.disabled = false;
        this.setWaitingStatus();
        return pads;
      }

      pads.forEach((pad) => {
        const option = document.createElement('option');
        option.value = String(pad.index);
        option.textContent = `[${pad.index}] ${this.controllerName(pad)}`;
        fragment.appendChild(option);
      });

      this.el.gamepadSelect?.appendChild(fragment);
      if (this.el.gamepadSelect) this.el.gamepadSelect.disabled = false;

      if (pads.some((pad) => pad.index === previous)) {
        this.selectedGamepadIndex = previous;
      } else {
        this.selectedGamepadIndex = pads[0].index;
      }

      if (this.el.gamepadSelect) this.el.gamepadSelect.value = String(this.selectedGamepadIndex);
      const pad = pads.find((item) => item.index === this.selectedGamepadIndex) || pads[0];
      this.updateConnectionMeta(pad, true);
      return pads;
    }

    controllerName(pad) {
      return String(pad?.id || this.config.unknown || 'Unknown controller').replace(/\s+/g, ' ').trim();
    }

    getCurrentGamepad() {
      const pads = this.getGamepads();
      if (!pads) return null;

      if (this.selectedGamepadIndex !== null && pads[this.selectedGamepadIndex]) {
        return pads[this.selectedGamepadIndex];
      }

      for (const pad of pads) {
        if (!pad) continue;
        this.selectedGamepadIndex = pad.index;
        if (this.el.gamepadSelect) this.el.gamepadSelect.value = String(pad.index);
        return pad;
      }

      return null;
    }

    setWaitingStatus() {
      this.currentPadSignature = '';
      this.el.connectionPill?.classList.remove('is-connected', 'is-error');
      if (this.el.connectionState) this.el.connectionState.textContent = this.config.waiting || 'Waiting for controller';
      if (this.el.connectionHint) this.el.connectionHint.textContent = this.config.connectHint || '';
      if (this.testedButtons.size) {
        this.testedButtons.clear();
        this.updateChecklist();
      }
      this.clearGamepadMeta();
    }

    setAccessError(message) {
      this.stopLoop();
      this.currentPadSignature = '';
      this.el.connectionPill?.classList.remove('is-connected');
      this.el.connectionPill?.classList.add('is-error');
      if (this.el.connectionState) this.el.connectionState.textContent = message || this.config.blocked || 'Controller access unavailable';
      if (this.el.connectionHint) this.el.connectionHint.textContent = '';
      if (this.el.gamepadSelect) this.el.gamepadSelect.disabled = true;
      if (this.testedButtons.size) {
        this.testedButtons.clear();
        this.updateChecklist();
      }
      this.clearGamepadMeta();
      this.clearVisuals();
      this.resetRawTables();
    }

    updateConnectionMeta(pad, force = false) {
      if (!pad) {
        this.setWaitingStatus();
        return;
      }

      const signature = [pad.index, pad.id, pad.mapping, pad.buttons.length, pad.axes.length].join('|');
      if (!force && signature === this.currentPadSignature) return;
      this.currentPadSignature = signature;

      this.el.connectionPill?.classList.remove('is-error');
      this.el.connectionPill?.classList.add('is-connected');
      if (this.el.connectionState) this.el.connectionState.textContent = this.config.connected || 'Controller connected';
      if (this.el.connectionHint) this.el.connectionHint.textContent = this.controllerName(pad);
      if (this.el.gamepadId) this.el.gamepadId.textContent = this.controllerName(pad);
      if (this.el.gamepadMapping) this.el.gamepadMapping.textContent = pad.mapping || '—';
      if (this.el.buttonCount) this.el.buttonCount.textContent = String(pad.buttons.length);
      if (this.el.axisCount) this.el.axisCount.textContent = String(pad.axes.length);
    }

    clearGamepadMeta() {
      if (this.el.gamepadId) this.el.gamepadId.textContent = '—';
      if (this.el.gamepadMapping) this.el.gamepadMapping.textContent = '—';
      if (this.el.buttonCount) this.el.buttonCount.textContent = '—';
      if (this.el.axisCount) this.el.axisCount.textContent = '—';
    }

    showToast(message) {
      if (!message || !this.el.toast) return;
      window.clearTimeout(this.toastTimer);
      this.el.toast.textContent = message;
      this.el.toast.classList.add('is-visible');
      this.toastTimer = window.setTimeout(() => {
        this.el.toast?.classList.remove('is-visible');
      }, 1400);
    }

    onSelectChange() {
      const value = this.el.gamepadSelect?.value ?? '';
      this.selectedGamepadIndex = value === '' ? null : Number(value);
      this.currentPadSignature = '';
      this.testedButtons.clear();
      this.updateChecklist();
      this.resetRawTables();

      const pad = this.getCurrentGamepad();
      if (pad) {
        this.updateConnectionMeta(pad, true);
        this.startLoop();
      }
      this.showToast(this.config.toastSwitched);
    }

    onReset() {
      this.testedButtons.clear();
      this.updateChecklist();
      this.showToast(this.config.toastReset);
    }

    onGamepadConnected(event) {
      const pads = this.refreshGamepadSelect();
      if (!pads) return;

      if (this.selectedGamepadIndex === null && event.gamepad) {
        this.selectedGamepadIndex = event.gamepad.index;
        if (this.el.gamepadSelect) this.el.gamepadSelect.value = String(this.selectedGamepadIndex);
      }

      const pad = this.getCurrentGamepad();
      if (pad) this.updateConnectionMeta(pad, true);
      this.showToast(this.config.toastConnected);
      this.startLoop();
    }

    onGamepadDisconnected(event) {
      if (event.gamepad && this.selectedGamepadIndex === event.gamepad.index) {
        this.selectedGamepadIndex = null;
        this.currentPadSignature = '';
        this.testedButtons.clear();
        this.updateChecklist();
        this.resetRawTables();
      }

      const pads = this.refreshGamepadSelect();
      if (!pads || !pads.length) {
        this.stopLoop();
        this.clearVisuals();
        this.resetRawTables();
      } else {
        this.startLoop();
      }
      this.showToast(this.config.toastDisconnected);
    }

    onVisibilityChange() {
      if (document.hidden) {
        this.stopLoop();
        return;
      }
      const pads = this.refreshGamepadSelect();
      if (pads && pads.length) this.startLoop();
    }

    onDiagnosticToggle() {
      if (!this.el.diagnosticDetails?.open) return;
      const pad = this.getCurrentGamepad();
      if (pad) this.ensureRawTables(pad);
    }

    startLoop() {
      if (this.destroyed || document.hidden || this.rafId) return;
      this.rafId = window.requestAnimationFrame(this.render);
    }

    stopLoop() {
      if (!this.rafId) return;
      window.cancelAnimationFrame(this.rafId);
      this.rafId = 0;
    }

    render() {
      this.rafId = 0;
      if (this.destroyed || document.hidden) return;

      let activePad = this.getCurrentGamepad();
      if (!activePad) {
        const pads = this.refreshGamepadSelect();
        if (!pads || !pads.length) {
          this.clearVisuals();
          this.resetRawTables();
          return;
        }
        activePad = this.getCurrentGamepad();
        if (!activePad) return;
      }

      this.updateConnectionMeta(activePad);
      const updateRaw = Boolean(this.el.diagnosticDetails?.open);
      if (updateRaw) this.ensureRawTables(activePad);

      this.clearHighlights();
      this.updateButtons(activePad, updateRaw);
      this.updateAxes(activePad, updateRaw);
      this.rafId = window.requestAnimationFrame(this.render);
    }

    updateChecklist() {
      const count = this.testedButtons.size;
      const pending = Math.max(0, TOTAL_TEST_BUTTONS - count);

      this.checkItems.forEach((item, index) => {
        const tested = this.testedButtons.has(index);
        item.classList.toggle('is-tested', tested);

        const target = tested ? this.el.completedList : this.el.pendingList;
        if (target) target.appendChild(item);
      });

      if (this.el.buttonProgress) this.el.buttonProgress.textContent = `${count} / ${TOTAL_TEST_BUTTONS}`;
      if (this.el.buttonProgressBar) this.el.buttonProgressBar.style.width = `${(count / TOTAL_TEST_BUTTONS) * 100}%`;
      if (this.el.pendingCount) this.el.pendingCount.textContent = String(pending);
      if (this.el.completedCount) this.el.completedCount.textContent = String(count);
      if (this.el.pendingEmpty) this.el.pendingEmpty.hidden = pending !== 0;
      if (this.el.completedSection) this.el.completedSection.hidden = count === 0;
      this.root.classList.toggle('is-complete', count === TOTAL_TEST_BUTTONS);
    }

    clearHighlights() {
      this.highlightParts.forEach((node) => node.classList.remove('is-active'));
      this.checkItems.forEach((item) => item.classList.remove('is-current'));
    }

    updateButtons(pad, updateRaw) {
      let checklistChanged = false;

      pad.buttons.forEach((button, index) => {
        const pressed = Boolean(button.pressed || button.value > 0.5);
        this.parts.get(`B${index}`)?.classList.toggle('is-active', pressed);

        const checklistItem = this.checkItems.get(index);
        if (checklistItem) {
          checklistItem.classList.toggle('is-current', pressed);
          if (pressed && !this.testedButtons.has(index)) {
            this.testedButtons.add(index);
            checklistChanged = true;
          }
        }

        if (updateRaw) {
          const raw = this.rawButtonRows[index];
          if (raw) {
            raw.row.classList.toggle('is-active', pressed);
            raw.pressed.textContent = String(Boolean(button.pressed));
            raw.value.textContent = Number(button.value || 0).toFixed(3);
          }
        }
      });

      if (checklistChanged) this.updateChecklist();
    }

    axisValue(pad, index, fallback = 0) {
      const value = pad.axes[index];
      return Number.isFinite(value) ? value : fallback;
    }

    decodeDpad(value) {
      if (!Number.isFinite(value)) return { key: '', symbol: '—' };
      if (value >= -1 && value <= -0.8) return { key: 'up', symbol: '↑' };
      if (value >= -0.5 && value <= -0.3) return { key: 'right', symbol: '→' };
      if (value >= 0.1 && value <= 0.3) return { key: 'down', symbol: '↓' };
      if (value >= 0.6 && value <= 1.0) return { key: 'left', symbol: '←' };
      return { key: '', symbol: '—' };
    }

    axisIsActive(index, value) {
      if (!Number.isFinite(value)) return false;
      if (index === 9) return Boolean(this.decodeDpad(value).key);
      return Math.abs(value) >= 0.08;
    }

    updateAxes(pad, updateRaw) {
      const lx = this.axisValue(pad, 0);
      const ly = this.axisValue(pad, 1);
      const rx = this.axisValue(pad, 2);
      const ry = this.axisValue(pad, 5);
      const axis9 = this.axisValue(pad, 9, NaN);
      const dpad = this.decodeDpad(axis9);

      this.setSvgStick(this.stickDots.left, STICK_CENTERS.left, lx, ly);
      this.setSvgStick(this.stickDots.right, STICK_CENTERS.right, rx, ry);
      this.setMonitorDot(this.el.leftMonitorDot, lx, ly);
      this.setMonitorDot(this.el.rightMonitorDot, rx, ry);

      if (this.el.leftReadout) this.el.leftReadout.textContent = `X ${lx.toFixed(3)} · Y ${ly.toFixed(3)}`;
      if (this.el.rightReadout) this.el.rightReadout.textContent = `X ${rx.toFixed(3)} · Y ${ry.toFixed(3)}`;
      if (this.el.leftAxisValues) this.el.leftAxisValues.textContent = `X ${lx.toFixed(4)} · Y ${ly.toFixed(4)}`;
      if (this.el.rightAxisValues) this.el.rightAxisValues.textContent = `X ${rx.toFixed(4)} · Y ${ry.toFixed(4)}`;
      if (this.el.dpadReadout) this.el.dpadReadout.textContent = dpad.symbol;
      if (this.el.axis9Direction) this.el.axis9Direction.textContent = dpad.symbol;
      if (this.el.axis9Value) this.el.axis9Value.textContent = Number.isFinite(axis9) ? axis9.toFixed(4) : '—';

      this.parts.get('DPAD_UP')?.classList.toggle('is-active', dpad.key === 'up');
      this.parts.get('DPAD_RIGHT')?.classList.toggle('is-active', dpad.key === 'right');
      this.parts.get('DPAD_DOWN')?.classList.toggle('is-active', dpad.key === 'down');
      this.parts.get('DPAD_LEFT')?.classList.toggle('is-active', dpad.key === 'left');

      let activeCount = 0;
      IMPORTANT_AXES.forEach((index) => {
        const value = index === 9 ? axis9 : this.axisValue(pad, index, NaN);
        const active = this.axisIsActive(index, value);
        if (active) activeCount += 1;

        const axisRow = this.axisRows.get(index);
        if (!axisRow) return;
        axisRow.row.classList.toggle('is-active', active);
        axisRow.value.textContent = Number.isFinite(value) ? value.toFixed(4) : '—';
        this.setAxisBar(axisRow.bar, index, value, active);
      });

      if (this.el.activeAxisCount) this.el.activeAxisCount.textContent = String(activeCount);

      if (updateRaw) {
        pad.axes.forEach((value, index) => {
          const raw = this.rawAxisRows[index];
          if (!raw) return;
          raw.row.classList.toggle('is-active', this.axisIsActive(index, value));
          raw.value.textContent = Number(value).toFixed(4);
        });
      }
    }

    setSvgStick(dot, center, x, y) {
      if (!dot) return;
      const clamp = (value) => Math.max(-1, Math.min(1, Number.isFinite(value) ? value : 0));
      const range = center.r * 0.34;
      dot.setAttribute('cx', (center.cx + clamp(x) * range).toFixed(2));
      dot.setAttribute('cy', (center.cy + clamp(y) * range).toFixed(2));
    }

    setMonitorDot(dot, x, y) {
      if (!dot) return;
      const clamp = (value) => Math.max(-1, Math.min(1, Number.isFinite(value) ? value : 0));
      dot.style.left = `${50 + clamp(x) * 40}%`;
      dot.style.top = `${50 + clamp(y) * 40}%`;
    }

    setAxisBar(bar, index, value, active) {
      if (!bar) return;

      if (index === 9) {
        bar.style.left = '50%';
        bar.style.width = active ? '50%' : '0';
        return;
      }

      if (!Number.isFinite(value)) {
        bar.style.left = '50%';
        bar.style.width = '0';
        return;
      }

      const normalized = Math.max(-1, Math.min(1, value));
      if (normalized >= 0) {
        bar.style.left = '50%';
        bar.style.width = `${normalized * 50}%`;
      } else {
        bar.style.left = `${50 + normalized * 50}%`;
        bar.style.width = `${Math.abs(normalized) * 50}%`;
      }
    }

    ensureRawTables(pad) {
      if (pad.buttons.length !== this.lastButtonCount) this.buildRawButtons(pad.buttons.length);
      if (pad.axes.length !== this.lastAxisCount) this.buildRawAxes(pad.axes.length);
    }

    buildRawButtons(count) {
      if (!this.el.rawButtons) return;
      this.el.rawButtons.textContent = '';
      this.rawButtonRows = [];
      const fragment = document.createDocumentFragment();
      for (let index = 0; index < count; index += 1) {
        const row = document.createElement('tr');
        const indexCell = this.createCell(`B${index}`);
        const labelCell = this.createCell(BUTTON_LABELS[index] || '—');
        const pressedCell = this.createCell('false');
        const valueCell = this.createCell('0.000');
        row.append(indexCell, labelCell, pressedCell, valueCell);
        fragment.appendChild(row);
        this.rawButtonRows[index] = { row, pressed: pressedCell, value: valueCell };
      }
      this.el.rawButtons.appendChild(fragment);
      this.lastButtonCount = count;
    }

    buildRawAxes(count) {
      if (!this.el.rawAxes) return;
      this.el.rawAxes.textContent = '';
      this.rawAxisRows = [];
      const fragment = document.createDocumentFragment();
      for (let index = 0; index < count; index += 1) {
        const row = document.createElement('tr');
        const indexCell = this.createCell(`Axis ${index}`);
        const roleCell = this.createCell(this.axisRole(index));
        const valueCell = this.createCell('0.0000');
        row.append(indexCell, roleCell, valueCell);
        fragment.appendChild(row);
        this.rawAxisRows[index] = { row, value: valueCell };
      }
      this.el.rawAxes.appendChild(fragment);
      this.lastAxisCount = count;
    }

    createCell(text) {
      const cell = document.createElement('td');
      cell.textContent = text;
      return cell;
    }

    axisRole(index) {
      if (index === 0) return `${this.config.leftStickLabel || 'Left Stick'} X`;
      if (index === 1) return `${this.config.leftStickLabel || 'Left Stick'} Y`;
      if (index === 2) return `${this.config.rightStickLabel || 'Right Stick'} X`;
      if (index === 5) return `${this.config.rightStickLabel || 'Right Stick'} Y`;
      if (index === 9) return this.config.dpadLabel || 'D-pad';
      return this.config.unused || 'Unused';
    }

    resetRawTables() {
      this.lastButtonCount = -1;
      this.lastAxisCount = -1;
      this.rawButtonRows = [];
      this.rawAxisRows = [];
      if (this.el.rawButtons) this.el.rawButtons.textContent = '';
      if (this.el.rawAxes) this.el.rawAxes.textContent = '';
    }

    clearVisuals() {
      this.clearHighlights();
      this.setSvgStick(this.stickDots.left, STICK_CENTERS.left, 0, 0);
      this.setSvgStick(this.stickDots.right, STICK_CENTERS.right, 0, 0);
      this.setMonitorDot(this.el.leftMonitorDot, 0, 0);
      this.setMonitorDot(this.el.rightMonitorDot, 0, 0);

      if (this.el.leftReadout) this.el.leftReadout.textContent = 'X 0.000 · Y 0.000';
      if (this.el.rightReadout) this.el.rightReadout.textContent = 'X 0.000 · Y 0.000';
      if (this.el.leftAxisValues) this.el.leftAxisValues.textContent = 'X 0.0000 · Y 0.0000';
      if (this.el.rightAxisValues) this.el.rightAxisValues.textContent = 'X 0.0000 · Y 0.0000';
      if (this.el.dpadReadout) this.el.dpadReadout.textContent = '—';
      if (this.el.axis9Direction) this.el.axis9Direction.textContent = '—';
      if (this.el.axis9Value) this.el.axis9Value.textContent = '—';
      if (this.el.activeAxisCount) this.el.activeAxisCount.textContent = '0';

      this.axisRows.forEach((axisRow, index) => {
        axisRow.row.classList.remove('is-active');
        axisRow.value.textContent = index === 9 ? '—' : '0.0000';
        if (axisRow.bar) {
          axisRow.bar.style.left = '50%';
          axisRow.bar.style.width = '0';
        }
      });
    }
  }

  function initRoot(root) {
    if (!root || instances.has(root)) return;
    const instance = new AISLPCControllerTester(root);
    instances.set(root, instance);
  }

  function initAll(scope = document) {
    if (scope.matches?.('[data-aislpc-controller-tester]')) initRoot(scope);
    scope.querySelectorAll?.('[data-aislpc-controller-tester]').forEach(initRoot);
  }

  function destroyWithin(scope) {
    for (const [root, instance] of instances) {
      if (root === scope || scope.contains?.(root)) {
        instance.destroy();
        instances.delete(root);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyWithin(event.target));
})();
